import { useQuery } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Suggestion, SuggestionEntity } from './types';

function getBoSuggestion({
  title,
  cycle,
  entity,
  enabled,
}: {
  title: string;
  cycle: string;
  entity: SuggestionEntity;
  enabled: boolean;
}) {
  if (
    title.length < 3 ||
    !enabled ||
    !['Cycle 1', 'Cycle 2', 'Cycle 3'].includes(cycle)
  ) {
    return Promise.resolve([]);
  }

  const requestURL = `/bo-suggest?title=${title}&cycle=${cycle}&entity=${entity}`;
  return request(requestURL, {
    method: 'GET',
  }).then(res => res?.suggestions || []);
}

export function useBoSuggestion({
  title,
  cycle,
  entity,
  enabled = false,
  skip = false,
}: {
  title?: string;
  cycle: string;
  entity: SuggestionEntity;
  enabled?: boolean;
  skip?: boolean;
}) {
  const [debouncedTitle, setDebouncedTitle] = useState<string | undefined>(
    title,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title);
    }, 500);

    return () => clearTimeout(timer);
  }, [title, cycle]);

  if (skip) {
    return { data: [], isLoading: false, isFetching: false };
  }

  // L'admin utilise ce composant et pour le moment doit utiliser react-query v4
  // alors que notre app utilise react-query v5
  // du coup le query provider râle, donc j'ai ajouté un skip
  // pour moi c'est safe car le skip ne changera pas lorsque le composant
  // est mount
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryResult = useQuery<Suggestion[], AxiosError, Suggestion[]>({
    queryKey: ['SUGGESTION', entity, cycle, debouncedTitle],
    queryFn: () =>
      getBoSuggestion({
        title: debouncedTitle || '',
        cycle,
        entity,
        enabled,
      }),
    staleTime: 1000 * 60 * 5, // Cache results for 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    gcTime: 1000 * 60 * 60,
    placeholderData: previousData => previousData, // Keep showing previous data while fetching
  });

  // Cela sert à cacher d'éventuelles suggestions déjà affichées avant que enabled passe à false,
  // en cas de changement du BO par exemple
  if (!enabled) {
    return { data: [], isLoading: false, isFetching: false };
  }

  // If we're fetching but have previous data, return the previous data to prevent flicker
  if (queryResult.isFetching && queryResult.data) {
    return {
      ...queryResult,
      data: queryResult.data,
      isFetching: false,
    };
  }

  return queryResult;
}
